'use client';

import { useEffect, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useActiveProject } from '@/features/project/hooks/use-active-project';
import { ProjectSettingsMenu } from './ProjectSettingsMenu';
import { useNewProjectStore } from '../model/new-project-store';
import { PanelBottom, Plus, Crown } from 'lucide-react';
import { House, User, Question } from '@phosphor-icons/react';
import { Button } from '@/shared/ui/shadcn/button';
import { capitalize } from '@/shared/lib/text';
import { cn } from '@/shared/lib/cn';
import { useJobQueueStore } from '@/features/design/job-queue';
import { HistoryActivityIcon } from '@/shared/ui/HistoryActivityIcon';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { useAccountIssue } from '@/shared/hooks/use-account-issue';
import { useRawTokenBalance } from '@/shared/api/hooks/use-token-balance';
import { formatTokenBalance } from '@/shared/lib/utils/tokenBalanceFormatter';
import { TokenBalanceBadge } from '@/shared/ui/TokenBalanceBadge';
import { useAccountChrome } from '@/shared/hooks/use-account-chrome';

/**
 * Unified left sidebar for desktop. The building is the app home, so it sits at
 * the top as its own standalone entry (the former "Dashboard" item is gone),
 * separated from the History / Account / Help block below.
 *
 * Collapsing hides the whole sidebar (Apple-style) — navigation then lives in
 * the floating bottom bar — so this component only ever renders expanded.
 */
export function AppSidebar() {
  const pathname = usePathname();
  const tNav = useTranslations('navigation');
  const tGrid = useTranslations('projectGrid');
  const tBuildings = useTranslations('dashboard');
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const openNewProject = useNewProjectStore((s) => s.open);

  // Active building — resolved identically across every nav surface (this
  // sidebar + the floating bottom bar) and persisted by <ActiveProjectSync/> in
  // the authed layout (this component only mounts on wide+expanded screens, so
  // it can't own the persistence). See `use-active-project.ts`.
  const { activeProjectId, activeProject } = useActiveProject();

  // Active only on the building page itself, not its sub-pages.
  const isProjectPage =
    !!activeProjectId && pathname === `/project/${activeProjectId}`;
  const isHistoryPage =
    pathname === '/history' || pathname.startsWith('/history/');
  const isHelpPage = pathname.startsWith('/help');
  const isAccountPage =
    pathname === '/account' || pathname.startsWith('/account/');

  const rawTokenBalance = useRawTokenBalance();

  const activeJobCount = useJobQueueStore((state) =>
    state.getProcessingJobCount()
  );
  const hasActiveJobs = activeJobCount > 0;
  const unseenCount = useJobQueueStore((state) =>
    state.getUnseenCompletedCount()
  );
  const markJobsSeen = useJobQueueStore((state) => state.markJobsSeen);

  const conceptIdFromUrl = useMemo(() => {
    const match = pathname.match(/\/concept\/([^/]+)/);
    return match?.[1] || null;
  }, [pathname]);

  useEffect(() => {
    if (isHistoryPage) {
      markJobsSeen();
    } else if (conceptIdFromUrl) {
      markJobsSeen(conceptIdFromUrl);
    }
  }, [isHistoryPage, conceptIdFromUrl, markJobsSeen]);

  const accountIssue = useAccountIssue();
  const tokensFlag = accountIssue.attentionRows?.tokens;
  const tokensIsBadge = tokensFlag === 'warning';

  // Post-pivot account-status chrome (P4.2/P4.3): legacy users keep the token
  // number; pack/free users see a status label and NEVER a token count. Scoped
  // to the currently-viewed building for the "Facade Pack" status.
  const accountChrome = useAccountChrome(activeProject);

  return (
    <div className="h-full flex flex-col">
      {/* Collapse toggle anchored top-right, mirroring Apple's macOS sidebar.
          Collapsing hides the sidebar entirely — re-expand from the bottom bar. */}
      <div className="flex-shrink-0 flex justify-end px-3 pt-4">
        <button
          onClick={toggleCollapsed}
          className="flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
          aria-label={tNav('toggleSidebar')}
        >
          <PanelBottom className="h-5 w-5 flex-shrink-0" />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto pt-2">
        {/* Building section — the standalone top entry (home). A plain link to
            the building page; switching buildings happens on the page itself
            (hero title switcher / ⋮ menu), not from navigation. */}
        {activeProjectId ? (
          <div className="flex-shrink-0 px-3">
            <Link
              href={`/project/${activeProjectId}`}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
                isProjectPage
                  ? 'bg-foreground/10 text-primary font-medium'
                  : 'text-foreground hover:bg-foreground/5'
              )}
            >
              <House
                className={cn(
                  'h-5 w-5 flex-shrink-0',
                  isProjectPage ? 'text-primary' : 'text-foreground'
                )}
                weight={isProjectPage ? 'fill' : 'regular'}
              />
              <span className="truncate">
                {activeProject
                  ? capitalize(activeProject.attributes.name)
                  : tBuildings('projects')}
              </span>
            </Link>
            <ProjectSettingsMenu projectId={activeProjectId} />
          </div>
        ) : (
          <div className="px-4 py-8 flex flex-col items-center gap-3 text-center text-muted-foreground text-list-item-subtitle">
            <Button size="sm" onClick={openNewProject}>
              <Plus className="h-4 w-4" />
              {tGrid('createFirst')}
            </Button>
          </div>
        )}

        {/* History / Account / Help — flows right after the building section. */}
        <nav className="mt-6 py-3 space-y-1.5 px-3">
          <Link
            href="/history"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
              isHistoryPage
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5'
            )}
          >
            <HistoryActivityIcon
              activeCount={activeJobCount}
              size="md"
              className={isHistoryPage ? 'text-primary' : 'text-foreground'}
              isActive={isHistoryPage}
            />
            <span>{tNav('history')}</span>
            {!hasActiveJobs && unseenCount > 0 && (
              <span className="ml-auto min-w-6 flex items-center justify-center flex-shrink-0">
                <span className="bg-primary text-primary-foreground px-1.5 py-0.5 text-chip rounded-full">
                  {unseenCount}
                </span>
              </span>
            )}
          </Link>
          <Link
            href="/account"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
              isAccountPage
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5'
            )}
          >
            {/* Paid-entitlement accounts (Builder Pass / Facade Pack): the gold
                crown REPLACES the person glyph entirely (mirrors the mobile tab
                bars) — the privilege IS the icon. The brand label lives on the
                Account page row itself, so the nav stays a compact glyph. */}
            {accountChrome.kind === 'builder_pass' ||
            accountChrome.kind === 'facade_pack' ? (
              <Crown
                className="h-5 w-5 flex-shrink-0 text-gold"
                fill={isAccountPage ? 'currentColor' : 'none'}
                aria-label={accountChrome.label}
              />
            ) : (
              <User
                className={cn(
                  'h-5 w-5 flex-shrink-0',
                  isAccountPage ? 'text-primary' : 'text-foreground'
                )}
                weight={isAccountPage ? 'fill' : 'regular'}
              />
            )}
            <span>{tNav('account')}</span>
            <div className="ml-auto flex items-center gap-2">
              {accountIssue.severity && !tokensIsBadge && (
                <span
                  className={cn(
                    'w-2 h-2 rounded-full animate-attention-pulse',
                    accountIssue.severity === 'alert'
                      ? 'bg-red-500'
                      : 'bg-amber-500'
                  )}
                />
              )}
              {/* Token numerics are LEGACY-only (P4.3) — pack/free users never
                  see a token count. Non-legacy users get a quiet status chip
                  (Facade Pack / "N of 3 free designs used") instead. */}
              {accountChrome.kind === 'tokens' &&
                rawTokenBalance !== undefined &&
                (tokensIsBadge ? (
                  <TokenBalanceBadge
                    balance={formatTokenBalance(rawTokenBalance)}
                    tokensFlag={tokensFlag}
                    className="text-chip"
                  />
                ) : (
                  <span className="rounded-full border border-border/60 px-2 py-0.5 text-chip tabular-nums text-muted-foreground">
                    {formatTokenBalance(rawTokenBalance)}
                  </span>
                ))}
              {(accountChrome.kind === 'pack' ||
                accountChrome.kind === 'free') &&
                accountChrome.label && (
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-chip whitespace-nowrap',
                      accountChrome.kind === 'pack'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'border border-border/60 text-muted-foreground tabular-nums'
                    )}
                  >
                    {/* Free tier: prefer the compact "{used}/{cap}" counter so the
                        chip never truncates in the narrow sidebar; the long
                        server label lives on the account detail page. */}
                    {accountChrome.kind === 'free'
                      ? (accountChrome.compactCounter ?? accountChrome.label)
                      : accountChrome.label}
                  </span>
                )}
            </div>
          </Link>
          <Link
            href="/help"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
              isHelpPage
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5'
            )}
          >
            <Question
              className={cn(
                'h-5 w-5 flex-shrink-0',
                isHelpPage ? 'text-primary' : 'text-foreground'
              )}
              weight={isHelpPage ? 'fill' : 'regular'}
            />
            <span>{tNav('help')}</span>
          </Link>
        </nav>
      </div>
    </div>
  );
}
